import { format, parseISO } from 'date-fns'

/**
 * Mapowanie surowych wpisów activity-log/audit-log z backendu (`IActivityLogEntryDto`)
 * na płaski model konsumowany przez UI (`IAuditLogEntryModel`) — ten sam, którego do tej
 * pory dostarczał mock `useCampaignGovernance`. Dzięki temu widoki (drawer Historia
 * kampanii, tabela Audit log, zakładka historii kodu) zostają bez zmian w kontrakcie.
 */

const DATETIME_FORMAT = 'dd.MM.yyyy, H:mm'
const NO_VALUE = '—'
const SYSTEM_CAUSER = 'System'

const PROMOTION_SUBJECT_TYPE = 'promotion'

// subject_type (BE) → typ wpisu w UI. Wszystko poza `promotion` traktujemy jako kampanię.
const subjectTypeToTab = (subjectType: string | null): EAuditLogType =>
	subjectType === PROMOTION_SUBJECT_TYPE ? EAuditLogType.DISCOUNT_CODE : EAuditLogType.CAMPAIGN

// Czytelne etykiety pól zmienianych w logu (klucze z properties.old/attributes lub changes).
const ACTIVITY_FIELD_LABELS: Record<string, string> = {
	workflow_status: 'Status',
	campaign_id: 'Kampania',
	name: 'Nazwa',
	description: 'Opis',
	starts_at: 'Start',
	ends_at: 'Koniec',
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
	value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null

const humanizeActivityValue = (key: string, value: unknown): string => {
	if (value === null || value === undefined || value === '') return NO_VALUE
	if (key === 'workflow_status') {
		const status = String(value)
		return CampaignStatusMap[status as ECampaignStatus] ?? CodeStatusMap[status as ECodeStatus] ?? status
	}
	return String(value)
}

const formatFieldChange = (key: string, before: unknown, after: unknown, hasBefore: boolean, hasAfter: boolean) => {
	const label = ACTIVITY_FIELD_LABELS[key] ?? key
	if (hasBefore && hasAfter)
		return `${label}: ${humanizeActivityValue(key, before)} → ${humanizeActivityValue(key, after)}`
	return `${label}: ${humanizeActivityValue(key, hasAfter ? after : before)}`
}

/**
 * Buduje czytelny opis zmian. Preferujemy `properties.old`/`properties.attributes`
 * (tak loguje backend), a w razie ich braku korzystamy z wyliczonego `changes`.
 */
const formatActivityChanges = (dto: IActivityLogEntryDto): string => {
	const before = asRecord(dto.properties?.old)
	const after = asRecord(dto.properties?.attributes)

	if (before || after) {
		const keys = Array.from(new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]))
		const parts = keys.map(key =>
			formatFieldChange(
				key,
				before?.[key],
				after?.[key],
				Boolean(before && key in before),
				Boolean(after && key in after),
			),
		)
		if (parts.length) return parts.join('; ')
	}

	if (dto.changes && Object.keys(dto.changes).length) {
		return Object.entries(dto.changes)
			.map(([key, value]) =>
				Array.isArray(value) && value.length === 2
					? formatFieldChange(key, value[0], value[1], true, true)
					: formatFieldChange(key, undefined, value, false, true),
			)
			.join('; ')
	}

	return NO_VALUE
}

const resolveActivityReason = (dto: IActivityLogEntryDto): string => {
	const reason = dto.properties?.change_reason ?? dto.properties?.rejection_reason
	return typeof reason === 'string' && reason.trim() ? reason : NO_VALUE
}

// Wpis nie nosi nazwy obiektu wprost — próbujemy z `properties`, w ostateczności pokazujemy id.
const resolveActivityObject = (dto: IActivityLogEntryDto): string => {
	const name = asRecord(dto.properties?.attributes)?.name ?? asRecord(dto.properties?.old)?.name
	if (typeof name === 'string' && name.trim()) return name
	return dto.subject_id ? `#${dto.subject_id}` : NO_VALUE
}

export const ActivityLogEntry = (dto: IActivityLogEntryDto): IAuditLogEntryModel => ({
	id: dto.id,
	date: dto.created_at ? format(parseISO(dto.created_at), DATETIME_FORMAT) : NO_VALUE,
	user: dto.causer?.name?.trim() || SYSTEM_CAUSER,
	type: subjectTypeToTab(dto.subject_type),
	object: resolveActivityObject(dto),
	action: dto.description || dto.event || NO_VALUE,
	changes: formatActivityChanges(dto),
	reason: resolveActivityReason(dto),
})

export const ActivityLogMeta = (meta?: IActivityLogMetaDto): IApiMetaModel => ({
	currentPage: meta?.current_page ?? null,
	from: null,
	lastPage: meta?.last_page ?? null,
	path: '',
	perPage: meta?.per_page ?? null,
	to: null,
	total: meta?.total ?? null,
})

/** Mapuje stronicowaną odpowiedź audit-log/activity-logs na `{ data, meta }` dla tabel/list UI. */
export const ActivityLogResponse = (response: IActivityLogResponseDto): IApiResponseModel<IAuditLogEntryModel[]> => ({
	data: (response?.data ?? []).map(ActivityLogEntry),
	meta: ActivityLogMeta(response?.meta),
})
